import { useCallback, useEffect, useSyncExternalStore } from 'react';

const drafts = new Map<string, string>();
const revisions = new Map<string, number>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

function read(key: string): string {
  if (!drafts.has(key)) {
    try { drafts.set(key, sessionStorage.getItem(key) ?? ''); }
    catch { drafts.set(key, ''); }
  }
  return drafts.get(key) ?? '';
}

function write(key: string, value: string) {
  drafts.set(key, value);
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch { /* In-memory drafts still work when storage is unavailable. */ }
}

export function readNewComposerDraftFolder(userId: number | undefined, entryMode: 'create-ad' | 'agent' = 'create-ad'): number | null {
  const key = `admoji-composer:v1:${userId ?? 'anonymous'}:${entryMode === 'agent' ? 'agent-new' : 'new'}`;
  if (!read(key).trim()) return null;
  const folder = Number(read(`${key}:folder`));
  return Number.isSafeInteger(folder) && folder > 0 ? folder : null;
}

/** Per-account, per-task text drafts survive navigation and refresh in this tab.
 * New-task text follows explicit brand switching; no brand is selected here.
 * A setter captures its scope so a late response cannot overwrite another task.
 */
export function useComposerDraft(userId: number | undefined, taskId: number | null, folderId: number | null, entryMode: 'create-ad' | 'agent' = 'create-ad') {
  const key = `admoji-composer:v1:${userId ?? 'anonymous'}:${taskId ?? (entryMode === 'agent' ? 'agent-new' : 'new')}`;
  const getSnapshot = useCallback(() => read(key), [key]);
  const text = useSyncExternalStore(subscribe, getSnapshot, () => '');
  useEffect(() => {
    // The first render can precede task/folder route hydration. Only adopt a
    // known destination here; explicit edits below clear an unassigned draft.
    if (taskId !== null || !text.trim() || folderId === null) return;
    write(`${key}:folder`, String(folderId));
  }, [key, text, taskId, folderId]);
  const setText = useCallback((value: string) => {
    revisions.set(key, (revisions.get(key) ?? 0) + 1);
    write(key, value);
    // Keep text and destination in the same captured scope, including a
    // failed submission restored after navigating to another task.
    if (taskId === null) write(`${key}:folder`, value.trim() && folderId !== null ? String(folderId) : '');
    listeners.forEach(listener => listener());
  }, [key, taskId, folderId]);
  const clearForSubmission = useCallback(() => {
    const submittedText = read(key);
    setText('');
    const clearedRevision = revisions.get(key);
    return () => {
      // A failed request can arrive after a new composer has mounted and
      // the user has started another brief in this same new-task slot.
      if (revisions.get(key) !== clearedRevision) return false;
      setText(submittedText);
      return true;
    };
  }, [key, setText]);
  return [text, setText, clearForSubmission] as const;
}
